import bindAllMethods from '../../../utils/bindAllMethods';
import AuthContextEnum from '../../authTokenService/AuthContextEnum';
import type IRefreshTokenService from './IRefreshTokenService';
import { RefreshParameterType } from '../ISessionService';
import { Lifecycle, inject, injectable, scoped } from 'tsyringe';
import { internalLogger } from '../../../interface/v1/clientOsLogger';
import { TenantStrategyEnum } from '../../tenantHandler/strategy/TenantStrategyEnum';
import IExchangeTokenService from '../exchangeTokenService/IExchangeTokenService';
import ExchangeTokenServiceNative from '../exchangeTokenService/ExchangeTokenServiceNative';
import * as JWebTypes from '../../JWeb/types';
import { TokenLifetimeRequirementType, TokenType } from '../../JWeb/JWebEnums';
import { AuthTokenService, IAuthTokenService } from '../../authTokenService';
import IApplicationService from '../../../services/applicationService/IApplicationService';
import { ApplicationContextEnum } from '../../../services/applicationService/enums';
import type { AuthProvidersPropsType } from '../../../infra/startup/types';
import AuthenticationProviderEnum from '../../../config/authenticationProviderEnum';
import { AuthPluginFacade } from '../../../services/JWeb';
import InitializationTokenCache from '../../../services/JWeb/cache/InitializationTokenCache';

@injectable()
@scoped(Lifecycle.ContainerScoped)
class RefreshTokenServiceNative implements IRefreshTokenService {
  private _refreshPromise: Promise<void>;
  private _authTokenService: IAuthTokenService;
  private _exchangeTokenServiceNative: IExchangeTokenService;
  private _applicationService: IApplicationService;
  private _authProviderProps: AuthProvidersPropsType;

  constructor(
    @inject(AuthTokenService)
    authTokenService: IAuthTokenService,
    @inject(ExchangeTokenServiceNative)
    exchangeTokenServiceNative: IExchangeTokenService,
    @inject('ApplicationService') applicationService: IApplicationService,
    @inject('AuthProviderProps')
    authProviderProps: AuthProvidersPropsType
  ) {
    this._authTokenService = authTokenService;
    this._exchangeTokenServiceNative = exchangeTokenServiceNative;
    this._applicationService = applicationService;
    this._authProviderProps = authProviderProps;

    bindAllMethods(this);
  }

  public async refresh(
    refreshParameterType?: RefreshParameterType
  ): Promise<void> {
    if (this._refreshPromise) return this._refreshPromise;

    const refreshAction = async () => {
      //TODO Fix why the setStratusAccessTokenFromNative is needed then remove this from here
      const authContext = await this._setStratusAccessTokenFromNative();

      // @TODO check if this is needed to pass for the exchange
      const tenantId = this._authTokenService.getTenantIdFromToken(authContext);

      if (!this._checkIfWebTokenIsRequired()) {
        await this._exchangeNativeTokenIntoWebToken(tenantId, authContext);
      }
    };

    this._refreshPromise = refreshAction().finally(() => {
      this._refreshPromise = undefined;
    });

    return this._refreshPromise;
  }

  private _checkIfWebTokenIsRequired(): boolean {
    const isNativeAppStratus =
      this._authProviderProps.initialAuthenticationProvider ===
      AuthenticationProviderEnum.authz;
    const isManifestStratus =
      this._authProviderProps.authenticationProvider ===
      AuthenticationProviderEnum.authz;
    return (
      isNativeAppStratus &&
      isManifestStratus &&
      this._applicationService.getApplicationContext() ===
        ApplicationContextEnum.NATIVE_PORTAL
    );
  }

  private async _exchangeNativeTokenIntoWebToken(
    tenantId: string,
    authContext: AuthContextEnum
  ): Promise<void> {
    await this._exchangeTokenServiceNative.exchange({
      tenantId: tenantId || null,
      authContext,
      tenantStrategy: TenantStrategyEnum.stratusNative,
      jwebTokenOptions: {
        tokenType: TokenType.user,
        requireFreshToken: true
      }
    });
  }

  private async _setStratusAccessTokenFromNative(): Promise<AuthContextEnum> {
    try {
      const tokenProviderOptions = {
        tokenType: TokenType.user,
        tokenLifetimeRequirements: [
          {
            type: TokenLifetimeRequirementType.preferredMinimumSecondsUntilExpiration,
            timeInterval: 60 * 60
          }
        ]
      } as JWebTypes.TokenProviderOptions;

      const { tokenValue } =
        ((await InitializationTokenCache.getToken()) as JWebTypes.AccessToken) ||
        ((await AuthPluginFacade.getToken({
          tokenProviderOptions
        })) as JWebTypes.AccessToken);

      const haveAccessToken =
        typeof tokenValue === 'string' && tokenValue.length > 1;

      /* istanbul ignore next */
      internalLogger.debug?.d4({
        className: 'RefreshTokenServiceNative',
        methodName: 'setStratusAccessTokenFromNative',
        message: 'Token received from AuthPluginFacade',
        variables: {
          tokenValue,
          haveAccessToken,
          tokenProviderOptions
        }
      });

      if (haveAccessToken) {
        const isTenantToken =
          !!this._authTokenService.getTenantIdFromGivenToken(tokenValue);

        const authContext = isTenantToken
          ? AuthContextEnum.tenant
          : AuthContextEnum.tenantless;

        /* istanbul ignore next */
        internalLogger.debug?.d1({
          className: 'RefreshTokenServiceNative',
          methodName: 'setStratusAccessTokenFromNative',
          variableName: 'tokenValue',
          message: `Token being set for authContext: ${authContext}`,
          currentValue: tokenValue
        });
        this._authTokenService.setToken(tokenValue, authContext);

        if (isTenantToken) {
          let tenantlessTokenValue = tokenValue;

          // Get orgless token when using authz provider
          if (
            this._applicationService.getAuthenticationProvider() ===
            AuthenticationProviderEnum.authz
          ) {
            tenantlessTokenValue = (
              (await AuthPluginFacade.getToken({
                tokenProviderOptions: {
                  ...tokenProviderOptions,
                  tenantID: null
                }
              })) as JWebTypes.AccessToken
            ).tokenValue;
          }

          /* istanbul ignore next */
          internalLogger.debug?.d1({
            className: 'RefreshTokenServiceNative',
            methodName: 'setStratusAccessTokenFromNative',
            variableName: 'tenantlessTokenValue',
            message: 'Token being set for tenantless context',
            currentValue: tenantlessTokenValue
          });
          this._authTokenService.setToken(
            tenantlessTokenValue,
            AuthContextEnum.tenantless
          );
        }

        return authContext;
      } else {
        /* istanbul ignore next */
        internalLogger.debug?.d1({
          className: 'RefreshTokenServiceNative',
          variableName: 'tokenValue',
          message: 'Invalid access token received',
          currentValue: tokenValue
        });
        this._authTokenService.deleteToken(AuthContextEnum.tenantless);
      }
    } catch (error) {
      this._authTokenService.deleteToken(AuthContextEnum.tenantless);
      /* istanbul ignore next */
      internalLogger.error.e3({
        className: 'RefreshTokenServiceNative',
        methodName: 'setStratusAccessTokenFromNative',
        message: 'Error setting Stratus access token from native',
        error: error
      });
      console.error(error);
      return;
    }
    return AuthContextEnum.tenantless;
  }
}

export default RefreshTokenServiceNative;
